import { useSnackbar } from 'notistack';
import { useForm } from 'react-hook-form';
import { ApolloQueryResult } from '@apollo/client';
import { yupResolver } from '@hookform/resolvers/yup';
import { useRef, useState, useEffect, useCallback } from 'react';

import { Stack } from '@mui/system';
import Box from '@mui/material/Box';
import Link from '@mui/material/Link';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import Divider from '@mui/material/Divider';
import Typography from '@mui/material/Typography';
import LoadingButton from '@mui/lab/LoadingButton';
import DialogTitle from '@mui/material/DialogTitle';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';

import {
  WhatsappConfigType,
  useTestWhatsappConnectionRequestMutation,
  useSaveWhatsappIntegrationMutation as useSaveWhatsappIntegration,
} from 'src/graphql/generated';

import Iconify from 'src/components/iconify';
import { RHFSwitch } from 'src/components/hook-form';
import FormProvider from 'src/components/hook-form/form-provider';

import { WhatsappConfigFormSchema, whatsappFormSchemaDefaultValues } from '../whatsapp-form-schema';

// ----------------------------------------------------------------------

declare global {
  interface Window {
    FB: any;
    fbAsyncInit: () => void;
  }
}

// ----------------------------------------------------------------------

type TAttributes =
  | 'whatsappAccessToken'
  | 'whatsappBusinessAccountId'
  | 'activated'
  | 'phoneNumberId';

type TCurrentData = Pick<WhatsappConfigType, TAttributes>;

type Props = {
  open: boolean;
  onClose: () => void;
  disableForm: boolean;
  currentData?: TCurrentData | null;
  refetch: () => Promise<ApolloQueryResult<any>>;
  hasData: boolean;
};

// ----------------------------------------------------------------------

export function ConfigWhatsappModal({
  open,
  onClose,
  currentData,
  refetch,
  disableForm,
  hasData,
}: Props) {
  const [isConnecting, setIsConnecting] = useState(false);
  const [isFacebookSDKLoaded, setIsFacebookSDKLoaded] = useState(false);
  const messageListenerRef = useRef<((event: MessageEvent) => void) | null>(null);
  const facebookAuthorizationCodeRef = useRef<string | null>(null);
  const pendingSignupDataRef = useRef<{
    phoneNumberId: string;
    whatsappBusinessAccountId: string;
  } | null>(null);

  const [saveWhatsappIntegration] = useSaveWhatsappIntegration();
  const [testWhatsappConnection, { loading: isTestingConnection }] =
    useTestWhatsappConnectionRequestMutation();
  const { enqueueSnackbar } = useSnackbar();

  const methods = useForm({
    resolver: yupResolver(WhatsappConfigFormSchema),
    defaultValues: whatsappFormSchemaDefaultValues,
  });

  const { reset, watch, setValue } = methods;

  const saveWhatsappData = useCallback(
    async (
      phoneNumberId: string,
      whatsappBusinessAccountId: string,
      facebookAuthorizationCode: string
    ) => {
      const variables = {
        input: {
          phoneNumberId,
          whatsappBusinessAccountId,
          facebookAuthorizationCode,
          activated: true,
        },
      };

      try {
        await saveWhatsappIntegration({ variables });
        enqueueSnackbar('Integração conectada com sucesso!', { variant: 'success' });
        await refetch();
        setIsConnecting(false);
      } catch (error: any) {
        setIsConnecting(false);
        if (error?.message) {
          enqueueSnackbar(error?.message, { variant: 'error' });
          return;
        }
        enqueueSnackbar('Não foi possível salvar os dados!', { variant: 'error' });
      }
    },
    [saveWhatsappIntegration, enqueueSnackbar, refetch]
  );

  const saveCompletedSignup = useCallback(
    (phoneNumberId: string, whatsappBusinessAccountId: string) => {
      const facebookAuthorizationCode = facebookAuthorizationCodeRef.current;

      if (!facebookAuthorizationCode) {
        pendingSignupDataRef.current = { phoneNumberId, whatsappBusinessAccountId };
        return;
      }

      pendingSignupDataRef.current = null;
      saveWhatsappData(phoneNumberId, whatsappBusinessAccountId, facebookAuthorizationCode);
    },
    [saveWhatsappData]
  );

  const handleToggleChange = useCallback(
    async (newValue: boolean) => {
      if (!hasData || !currentData) return;

      const variables = {
        input: {
          phoneNumberId: currentData.phoneNumberId || '',
          whatsappBusinessAccountId: currentData.whatsappBusinessAccountId || '',
          activated: newValue,
        },
      };

      try {
        await saveWhatsappIntegration({ variables });

        enqueueSnackbar(
          newValue ? 'Serviço ativado com sucesso!' : 'Serviço desativado com sucesso!',
          { variant: 'success' }
        );

        await refetch();
      } catch (error: any) {
        // Reverter o valor em caso de erro
        setValue('activated', !newValue);

        if (error?.message) {
          enqueueSnackbar(error?.message, { variant: 'error' });

          return;
        }
        enqueueSnackbar('Não foi possível atualizar o status!', { variant: 'error' });
      }
    },
    [hasData, currentData, saveWhatsappIntegration, enqueueSnackbar, refetch, setValue]
  );

  const handleClose = () => {
    onClose();
    reset();
    setIsConnecting(false);
    facebookAuthorizationCodeRef.current = null;
    pendingSignupDataRef.current = null;
  };

  const handleTestConnection = async () => {
    try {
      const { data } = await testWhatsappConnection();

      if (data?.testWhatsappConnection?.ok) {
        const businessName = data?.testWhatsappConnection?.businessName;
        const currency = data?.testWhatsappConnection?.currency;
        const successMessage =
          businessName && currency
            ? `Conexão validada: ${businessName} (${currency})`
            : data?.testWhatsappConnection?.message || 'Conexão validada com sucesso.';

        enqueueSnackbar(successMessage, { variant: 'success' });

        return;
      }

      enqueueSnackbar(
        data?.testWhatsappConnection?.message || 'Não foi possível validar a conexão.',
        { variant: 'error' }
      );
    } catch (error: any) {
      if (error?.message) {
        enqueueSnackbar(error?.message, { variant: 'error' });

        return;
      }

      enqueueSnackbar('Não foi possível validar a conexão.', { variant: 'error' });
    }
  };

  const loadFacebookSDK = () => {
    if (window.FB) {
      setIsFacebookSDKLoaded(true);
      return;
    }

    window.fbAsyncInit = () => {
      window.FB.init({
        appId: '646230174827862',
        autoLogAppEvents: true,
        xfbml: true,
        version: 'v22.0',
      });
      setIsFacebookSDKLoaded(true);
    };

    // Load SDK asynchronously
    if (!document.getElementById('facebook-jssdk')) {
      const fjs = document.getElementsByTagName('script')[0];
      const js = document.createElement('script') as HTMLScriptElement;
      js.id = 'facebook-jssdk';
      js.src = 'https://connect.facebook.net/en_US/sdk.js';
      fjs.parentNode?.insertBefore(js, fjs);
    }
  };

  const launchWhatsAppSignup = () => {
    if (!window.FB) {
      enqueueSnackbar('SDK do Facebook não carregado. Aguarde um momento e tente novamente.', {
        variant: 'error',
      });
      return;
    }

    setIsConnecting(true);
    facebookAuthorizationCodeRef.current = null;
    pendingSignupDataRef.current = null;

    const fbLoginCallback = (response: any) => {
      const facebookAuthorizationCode = response.authResponse?.code;

      if (facebookAuthorizationCode) {
        facebookAuthorizationCodeRef.current = facebookAuthorizationCode;
        const pendingSignupData = pendingSignupDataRef.current;

        if (pendingSignupData) {
          saveCompletedSignup(
            pendingSignupData.phoneNumberId,
            pendingSignupData.whatsappBusinessAccountId
          );
        }

        return;
      }

      setIsConnecting(false);
      pendingSignupDataRef.current = null;
      enqueueSnackbar('Não foi possível obter o código de autorização do Facebook.', {
        variant: 'error',
      });
    };

    window.FB.login(fbLoginCallback, {
      config_id: '649685391400994',
      response_type: 'code',
      override_default_response_type: true,
      redirect_uri: `${window.location.origin}/dashboard/config`,
      extras: {
        version: 'v4',
        sessionInfoVersion: '3',
        featureType: 'whatsapp_business_app_onboarding',
        features: [{ name: 'app_only_install' }],
      },
    });
  };

  // Load Facebook SDK when modal opens
  useEffect(() => {
    if (open) {
      loadFacebookSDK();
    }
  }, [open]);

  // Setup postMessage listener when modal opens
  useEffect(() => {
    if (!open) {
      return undefined;
    }

    const handleMessage = (event: MessageEvent) => {
      if (
        event.origin !== 'https://www.facebook.com' &&
        event.origin !== 'https://web.facebook.com'
      ) {
        return;
      }

      try {
        const data = JSON.parse(event.data);
        console.log('Data:', data);
        if (data.type === 'WA_EMBEDDED_SIGNUP') {
          const isFinishEvent =
            data.event === 'FINISH' || data.event === 'FINISH_WHATSAPP_BUSINESS_APP_ONBOARDING';

          if (isFinishEvent) {
            const { phone_number_id, waba_id } = data.data;
            console.log(
              'Phone number ID ',
              phone_number_id,
              ' WhatsApp business account ID ',
              waba_id
            );

            if (phone_number_id && waba_id) {
              saveCompletedSignup(phone_number_id, waba_id);
            } else {
              setIsConnecting(false);
              pendingSignupDataRef.current = null;
              enqueueSnackbar('Dados incompletos recebidos do Facebook', { variant: 'error' });
            }
          } else if (data.event === 'CANCEL') {
            const { current_step } = data.data;
            setIsConnecting(false);
            facebookAuthorizationCodeRef.current = null;
            pendingSignupDataRef.current = null;
            enqueueSnackbar('Conexão cancelada pelo usuário', { variant: 'warning' });
            console.warn('Cancel at ', current_step);
          } else if (data.event === 'ERROR') {
            const { error_message } = data.data;
            setIsConnecting(false);
            facebookAuthorizationCodeRef.current = null;
            pendingSignupDataRef.current = null;
            enqueueSnackbar(`Erro ao conectar: ${error_message || 'Erro desconhecido'}`, {
              variant: 'error',
            });
            console.error('error ', error_message);
          }
        }
      } catch {
        // Non JSON responses - ignore
      }
    };

    messageListenerRef.current = handleMessage;
    window.addEventListener('message', handleMessage);

    // eslint-disable-next-line consistent-return
    return function cleanup() {
      window.removeEventListener('message', handleMessage);
      messageListenerRef.current = null;
    };
  }, [open, enqueueSnackbar, saveCompletedSignup]);

  const activatedValue = watch('activated');
  const previousActivatedRef = useRef<boolean | undefined>(undefined);
  const isInitialMountRef = useRef(true);

  useEffect(() => {
    const updateFields = () => {
      if (!currentData || !hasData) {
        previousActivatedRef.current = undefined;
        isInitialMountRef.current = true;
        return;
      }

      const activated = currentData?.activated ?? false;
      reset({
        whatsappBusinessAccountId: currentData?.whatsappBusinessAccountId || '',
        phoneNumberId: currentData?.phoneNumberId || '',
        activated,
      });
      previousActivatedRef.current = activated;
      isInitialMountRef.current = true;
    };

    updateFields();
  }, [currentData, reset, hasData]);

  // Salvar automaticamente quando o toggle mudar
  useEffect(() => {
    if (
      !hasData ||
      !currentData ||
      isInitialMountRef.current ||
      activatedValue === undefined ||
      previousActivatedRef.current === undefined
    ) {
      // Ignorar a primeira renderização após atualizar os campos
      if (previousActivatedRef.current !== undefined) {
        isInitialMountRef.current = false;
      }
      return;
    }

    // Só salva se o valor realmente mudou
    if (activatedValue !== previousActivatedRef.current) {
      handleToggleChange(activatedValue);
      previousActivatedRef.current = activatedValue;
    }
  }, [activatedValue, hasData, currentData, handleToggleChange]);

  const renderImportantLinks = () => (
    <Stack spacing={2.5} sx={{ width: '100%', pt: 1 }}>
      <Stack spacing={1}>
        <Typography variant="body2" color="text.secondary">
          A conta comercial precisa estar verificada para a Meta aumentar a quantidade de envios no
          mês.
        </Typography>
        <Link
          href="https://business.facebook.com/latest/settings/security_center"
          target="_blank"
          rel="noopener"
          underline="always"
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 0.5,
            color: 'primary.main',
            fontWeight: 500,
          }}
        >
          Verificar conta comercial do Facebook
          <Iconify icon="eva:external-link-fill" width={14} />
        </Link>
      </Stack>

      <Stack spacing={1}>
        <Typography variant="body2" color="text.secondary">
          O pagamento precisa estar ativo para conseguir começar a enviar mensagens.
        </Typography>
        <Link
          href="https://business.facebook.com/settings/payment-methods"
          target="_blank"
          rel="noopener"
          underline="always"
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 0.5,
            color: 'primary.main',
            fontWeight: 500,
          }}
        >
          Adicionar método de pagamento
          <Iconify icon="eva:external-link-fill" width={14} />
        </Link>
      </Stack>
    </Stack>
  );

  return (
    <FormProvider methods={methods}>
      <Dialog
        open={open}
        PaperProps={{
          sx: {
            minWidth: { xs: '90%', md: 600 },
          },
        }}
      >
        <DialogTitle
          textAlign="center"
          fontSize={20}
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 1,
          }}
        >
          <Iconify icon="simple-icons:meta" width={24} sx={{ color: '#0081FB' }} />
          API Oficial do Meta
        </DialogTitle>

        <DialogContent
          sx={{
            width: '100%',
            minHeight: 180,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            gap: 2,
            marginBottom: 2,
            py: 2,
          }}
        >
          {!hasData ? (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'flex-start',
                justifyContent: 'center',
                gap: 3,
                py: 2,
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  width: '100%',
                  gap: 2,
                }}
              >
                <Typography variant="body1" textAlign="center" color="text.secondary">
                  Conecte sua conta do WhatsApp Business através do Facebook para começar a usar a
                  API Oficial do Meta.
                </Typography>
                <LoadingButton
                  variant="contained"
                  onClick={launchWhatsAppSignup}
                  loading={isConnecting}
                  disabled={!isFacebookSDKLoaded || isConnecting}
                  sx={{
                    backgroundColor: '#1877f2',
                    '&:hover': {
                      backgroundColor: '#166fe5',
                    },
                    height: 40,
                    px: 4,
                    fontWeight: 'bold',
                  }}
                >
                  {isConnecting ? 'Conectando...' : 'Conectar com Facebook'}
                </LoadingButton>
              </Box>

              {renderImportantLinks()}
            </Box>
          ) : (
            <>
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  width: '100%',
                  gap: 2,
                  py: 2,
                }}
              >
                <LoadingButton
                  variant="contained"
                  onClick={launchWhatsAppSignup}
                  loading={isConnecting}
                  disabled={!isFacebookSDKLoaded || isConnecting}
                  sx={{
                    backgroundColor: '#1877f2',
                    '&:hover': {
                      backgroundColor: '#166fe5',
                    },
                    height: 40,
                    px: 4,
                    fontWeight: 'bold',
                  }}
                >
                  {isConnecting ? 'Conectando...' : 'Reconectar com Facebook'}
                </LoadingButton>
              </Box>

              <Stack spacing={1.5} sx={{ width: '100%', pt: 1 }}>
                <Typography variant="subtitle2" fontWeight={600}>
                  Gerenciar no Facebook Business
                </Typography>
                <Stack spacing={1}>
                  <Link
                    href="https://business.facebook.com/latest/settings/whatsapp_account"
                    target="_blank"
                    rel="noopener"
                    underline="always"
                    sx={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 0.5,
                      color: 'primary.main',
                      fontWeight: 500,
                    }}
                  >
                    Visualizar contas do WhatsApp
                    <Iconify icon="eva:external-link-fill" width={14} />
                  </Link>
                  <Link
                    href="https://business.facebook.com/latest/whatsapp_manager/phone_numbers/"
                    target="_blank"
                    rel="noopener"
                    underline="always"
                    sx={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 0.5,
                      color: 'primary.main',
                      fontWeight: 500,
                    }}
                  >
                    Gerenciador de WhatsApp
                    <Iconify icon="eva:external-link-fill" width={14} />
                  </Link>
                </Stack>
              </Stack>

              {renderImportantLinks()}
            </>
          )}
        </DialogContent>

        <Divider sx={{ borderStyle: 'dashed' }} />

        <DialogActions
          sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}
        >
          {hasData && (
            <RHFSwitch
              name="activated"
              label={watch('activated') ? 'Serviço Ativo' : 'Serviço Inativo'}
            />
          )}
          {!hasData && <Box />}
          <Stack sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 2 }}>
            {hasData && (
              <LoadingButton
                variant="outlined"
                color="primary"
                onClick={handleTestConnection}
                loading={isTestingConnection}
              >
                Testar
              </LoadingButton>
            )}
            <Button variant="outlined" color="inherit" onClick={handleClose}>
              Fechar
            </Button>
          </Stack>
        </DialogActions>
      </Dialog>
    </FormProvider>
  );
}
